import {AfterViewInit, Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import { Subject } from 'rxjs';
import StatusResponse = facebook.StatusResponse;
import {InstagramService} from "../../services/instagram.service";
import { FacebookSdkService } from '../../cloud-whatsapp/config-whatsapp/facebook-sdk.service';


declare const FB: any;

@Component({
  selector: 'app-config-instagram',
  templateUrl: './config-instagram.component.html',
  styleUrls: ['./config-instagram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigInstagramComponent implements OnInit, AfterViewInit, OnDestroy {
  conectado = false;
  contas: Array<any>;
  accessToken: string;
  contaSelecionada: any;
  carregandoInstagram = false;
  mensagem = '';
  msgCarregando = '';
  dadosInstagram: any;
  nenhumaPaginaEncontrada = false;
  contaConectando: any = null;
  desconectando = false;
  fbError = false; // Flag para indicar erro com o SDK do Facebook

  private destroy$ = new Subject<void>();

  constructor(
    private instagramService: InstagramService,
    private fbService: FacebookSdkService,
    private cdr: ChangeDetectorRef
  ) {
    // ✅ Inicializa variáveis para evitar problemas de estado
    this.contas = null;
    this.contaSelecionada = null;
    this.dadosInstagram = null;
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.instagramService.obtenhaContaConectada().then((contaInsta) => {
      this.dadosInstagram = contaInsta;

      // Aguardar o SDK estar pronto antes de verificar status
      this.aguardarSDKEVerificarStatus(contaInsta);
    }).catch(erro => {
      console.error('Erro ao obter conta conectada:', erro);
      this.cdr.markForCheck();
    });
  }

  private async aguardarSDKEVerificarStatus(contaInsta: any): Promise<void> {
    try {
      // Aguardar um pouco para o SDK carregar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Verificar se o FB está disponível
      if (typeof FB === 'undefined') {
        console.warn('SDK do Facebook não está disponível');
        this.fbError = true;
        this.cdr.markForCheck();
        return;
      }

      // Agora verificar o status de login
      FB.getLoginStatus((response) => {
        if (response.status === "connected") {
          this.conectado = true;
          this.contaSelecionada = contaInsta;

          // ✅ Se está conectado mas não tem conta selecionada, tenta recarregar páginas
          if (!this.contaSelecionada && this.dadosInstagram?.accessToken) {
            this.tentaRecarregarPaginas();
          } else if (!this.contaSelecionada && !this.dadosInstagram?.accessToken) {
            // ✅ Se não tem token, força reconexão
            this.conectado = false;
          }

          if (this.contaSelecionada) {
            this.mensagem = 'Chatbot instalado. Teste conversando com sua conta no instagram.'
          }
        } else if (response.status === 'unknown') {
          this.conectado = false;
          if (contaInsta) {
            this.desconectar();
          }
        }

        this.cdr.markForCheck();

        setTimeout(() => {
          FB.XFBML.parse();
        });
      });
    } catch (erro) {
      console.error('Erro ao aguardar SDK:', erro);
      this.fbError = true;
      this.cdr.markForCheck();
    }
  }

  private tentaRecarregarPaginas() {
    if (this.dadosInstagram?.accessToken) {
      this.carregandoInstagram = true;
      this.msgCarregando = 'Recarregando suas páginas do Facebook...';
      this.contas = null; // ✅ Limpa contas para forçar recarregamento
      this.cdr.markForCheck();

      this.obtenhaContasInstagram(this.dadosInstagram.accessToken)
        .then(() => {
          // ✅ Se não encontrou contas ou não tem conta selecionada, força reconexão
          if (!this.contas || this.contas.length === 0) {
            this.conectado = false;
            this.carregandoInstagram = false;
          }
          this.cdr.markForCheck();
        })
        .catch((erro) => {
          console.error('Erro ao recarregar páginas:', erro);
          this.carregandoInstagram = false;
          this.conectado = false; // ✅ Força reconexão se falhar
          this.contas = null;
          this.cdr.markForCheck();
        });
    } else {
      // ✅ Se não tem token, força reconexão
      this.conectado = false;
      this.cdr.markForCheck();
    }
  }

  async desconectar() {
    try {
      this.desconectando = true;
      this.cdr.markForCheck();

      await this.instagramService.desconectarConta();

      FB.logout((response) => {
        this.conectado = false;
        this.contaSelecionada = null;
        this.desconectando = false;
        this.contas = null;
        this.accessToken = null;
        this.dadosInstagram = null;
        this.cdr.markForCheck();

        setTimeout(() => {
          FB.XFBML.parse();
        })
      });
    } catch (erro) {
      this.desconectando = false;
      console.error('Erro ao desconectar:', erro);
      this.cdr.markForCheck();
    }
  }

  atualizarToken() {
    this.contaSelecionada = null;
    this.contas = null; // ✅ Limpa contas para forçar recarregamento

    if (this.dadosInstagram && this.dadosInstagram.accessToken) {
      this.carregandoInstagram = true;
      this.msgCarregando = 'Atualizando informações das páginas...';
      this.cdr.markForCheck();

      this.obtenhaContasInstagram(this.dadosInstagram.accessToken)
        .catch((erro) => {
          console.error('Erro ao atualizar token:', erro);
          this.carregandoInstagram = false;
          this.conectado = false; // ✅ Força reconexão se falhar
          this.cdr.markForCheck();
        });
    } else {
      this.conectar();
    }
  }

  async conectar() {
    try {
      this.contaSelecionada = null;
      this.contas = null;
      this.mensagem = '';
      this.nenhumaPaginaEncontrada = false;
      this.fbError = false;
      this.cdr.markForCheck();

      // Verificar se o SDK está disponível
      if (typeof FB === 'undefined') {
        this.fbError = true;
        this.cdr.markForCheck();
        return;
      }

      // Usar FB.login diretamente para Instagram
      FB.login((response: StatusResponse) => {
        // ✅ Verifica se conectou E se tem authResponse válido
        if (response.status === "connected" && response.authResponse?.accessToken) {
          this.conectado = true;
          this.carregandoInstagram = true;
          this.msgCarregando = 'Processando autenticação do Facebook...';
          this.cdr.markForCheck();

          this.instagramService.estendaToken(response.authResponse.accessToken).then((resposta) => {
            this.conectou(resposta);
          }).catch((erro) => {
            this.carregandoInstagram = false;
            this.conectado = false;
            console.error('Erro ao estender token:', erro);
            this.cdr.markForCheck();
          });

        } else {
          // ✅ Qualquer outro caso: cancelou, negou permissão, erro, etc.
          this.conectado = false;
          this.carregandoInstagram = false;
          console.log('Login não realizado. Status:', response.status);
          this.cdr.markForCheck();
          return;
        }

        setTimeout(() => {
          FB.XFBML.parse();
        });
      }, {
        scope: 'email,public_profile,instagram_basic,instagram_manage_messages,pages_show_list,pages_manage_metadata'
      });
    } catch (erro) {
      console.error('Erro ao conectar com Facebook:', erro);
      this.fbError = true;
      this.conectado = false;
      this.carregandoInstagram = false;
      this.cdr.markForCheck();
    }
  }

  conectou(accessToken: string) {
    this.accessToken = accessToken;
    this.msgCarregando = 'Buscando suas páginas do Facebook...';
    this.cdr.markForCheck();

    this.obtenhaContasInstagram(this.accessToken).then((resp) => {
      const contasBusiness = this.contas?.filter(c => c.status === 'business_ativo') || [];

      if (contasBusiness.length === 1) {
        const conta = contasBusiness[0];
        this.mensagem = "Instalando chatbot no instagram selecionado."
        this.cdr.markForCheck();

        this.instagramService.obtenhaTokenDePagina(conta, accessToken, conta.tokenDePagina)
          .then((resposta) => {
            this.contaSelecionada = conta;
            this.mensagem = 'Chatbot instalado. Teste conversando com sua conta no instagram.';
            this.cdr.markForCheck();
          })
          .catch((erro) => {
            console.error('Erro ao obter token de página:', erro);
            this.mensagem = 'Erro ao configurar chatbot. Por favor, tente novamente.';
            this.carregandoInstagram = false;
            this.cdr.markForCheck();
          });
      }
    }).catch((erro) => {
      this.carregandoInstagram = false;
      this.conectado = false;
      this.contas = null;
      console.error('Erro ao obter contas Instagram:', erro);
      this.cdr.markForCheck();
    });
  }

  obtenhaContasInstagram(accessToken: string): Promise<any> {
    this.carregandoInstagram = true;
    this.nenhumaPaginaEncontrada = false;
    this.cdr.markForCheck();

    return new Promise((resolve => {
      this.contas = [];

      if (!this.msgCarregando) {
        this.msgCarregando = 'Buscando suas páginas do Facebook...';
      }

      FB.api(
        '/me/accounts?fields=id,name,username,access_token,category,category_list,' +
        'instagram_business_account&access_token=' + accessToken, (response: any) => {

        const paginas = response.data;

        if (!paginas || paginas.length === 0) {
          this.carregandoInstagram = false;
          this.nenhumaPaginaEncontrada = true;
          this.cdr.markForCheck();
          resolve(null);
          return;
        }

        let requests = [];
        let paginasComInstagram = [];

        for (let i = 0; i < paginas.length; i++) {
          const pagina = paginas[i];

          if (pagina.instagram_business_account) {
            const igId = pagina.instagram_business_account.id;
            requests.push({method: 'GET', relative_url: igId + '?fields=name,profile_picture_url'});
            paginasComInstagram.push({
              ...pagina,
              indiceRequest: requests.length - 1,
              temInstagram: true,
              ehBusiness: true
            });
          } else {
            this.contas.push({
              idPagina: pagina.id,
              nomePagina: pagina.name,
              tokenDePagina: pagina.access_token,
              temInstagram: false,
              ehBusiness: false,
              status: 'sem_instagram',
              mensagemStatus: 'Instagram não conectado a esta página',
              tipoAcao: 'conectar'
            });
          }
        }

        if (requests.length === 0) {
          this.carregandoInstagram = false;
          this.cdr.markForCheck();
          resolve(null);
          return;
        }

        this.msgCarregando = 'Verificando contas do Instagram...';
        this.cdr.markForCheck();

        FB.api('/?access_token=' + accessToken, 'post', {
          batch: requests
        }, (responseInsta: any) => {
          this.carregandoInstagram = false;

          for (let i = 0; i < responseInsta.length; i++) {
            const respInsta = responseInsta[i];
            const paginaCorrespondente = paginasComInstagram.find(p => p.indiceRequest === i);

            if (!paginaCorrespondente) continue;

            if (respInsta.code === 200) {
              const contaInstagram = JSON.parse(respInsta.body);
              this.contas.push({
                tokenDePagina: paginaCorrespondente.access_token,
                nomeInstagram: contaInstagram.name,
                nomePagina: paginaCorrespondente.name,
                igid: paginaCorrespondente.instagram_business_account.id,
                imageProfileUrl: contaInstagram.profile_picture_url,
                idPagina: paginaCorrespondente.id,
                temInstagram: true,
                ehBusiness: true,
                status: 'business_ativo',
                mensagemStatus: 'Conta Business ativa',
                tipoAcao: 'selecionar'
              });
            } else {
              this.contas.push({
                idPagina: paginaCorrespondente.id,
                nomePagina: paginaCorrespondente.name,
                tokenDePagina: paginaCorrespondente.access_token,
                temInstagram: true,
                ehBusiness: false,
                status: 'instagram_pessoal',
                mensagemStatus: 'Instagram pessoal - Precisa converter para Business',
                tipoAcao: 'converter'
              });
            }
          }

          this.cdr.markForCheck();
          resolve(null);
        });
      });
    }));
  }

  selecionouInstagram(conta: any) {
    this.contaConectando = conta;
    conta.msgErro = null;
    this.cdr.markForCheck();

    this.instagramService.obtenhaTokenDePagina(conta, this.accessToken, conta.tokenDePagina)
      .then((resposta) => {
        this.contaSelecionada = conta;
        this.contaConectando = null;
        this.mensagem = 'Chatbot instalado com sucesso! Teste conversando com sua conta no Instagram.';
        this.cdr.markForCheck();
      })
      .catch(erro => {
        conta.msgErro = erro;
        this.contaConectando = null;
        this.cdr.markForCheck();
      });
  }

  // Método para recarregar a página
  public reloadPage(): void {
    window.location.reload();
  }
}
